import React, { FC } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import * as S from './DesktopFooter.styles';
import { Typography } from '@/components/UI/Typography/Typography.styles';
import { ChangeLang } from '@/components/ChangeLang/ChangeLang';
import { makeHrefForPhone } from '@/utils/helpers';
import { CONTACT_US } from '@/constants/contactUs';
import { ROUTES } from '@/constants/routes';
import NoSsr from '@/components/NoSsr/NoSsr';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { LogoLink } from '@/components/LogoLink/LogoLink';
import { GET_REGIONS } from '@/api/queries/regions.queries';
import { GetRegionsResponse, Region } from '@/api/types/regions.types';
import { regionsMock } from '@/__e2e__/mock/regionsMock';
import { useRegionsStore } from '@/store/useRegionsStore';
import { QUERY } from '@/constants/common';

interface DesktopFooterProps {
  className?: string;
}

export const DesktopFooter: FC<DesktopFooterProps> = ({ className }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { topRegions: topRegionsMock, otherRegions: otherRegionsMock } =
    regionsMock(language);

  const isBrowser = typeof window !== 'undefined';
  const setRegions = useRegionsStore(s => s.setRegions);
  const regionsData = useRegionsStore(s => s.regions[language] ?? null);

  useQuery<GetRegionsResponse>(GET_REGIONS, {
    skip: Boolean(regionsData) || !isBrowser,
    onCompleted: data => {
      setRegions(language, data);
    },
  });

  const topRegions = regionsData?.topRegions ?? topRegionsMock;
  const otherRegions = regionsData?.otherRegions ?? otherRegionsMock;

  function getRegionUrl(region: Region) {
    const params = new URLSearchParams();
    params.append(`${QUERY.q}[${QUERY.regionId}]`, region.legacyResourceId);
    return `/elanlar?${params.toString()}`;
  }

  return (
    <S.FooterBox className={className} data-testid="footer">
      <S.Regions>
        <S.TopRegions>
          {topRegions.map(region => (
            <li key={region.legacyResourceId}>
              <S.RegionLink href={getRegionUrl(region)}>
                {region.name}
              </S.RegionLink>
            </li>
          ))}
        </S.TopRegions>
        <S.OtherRegions>
          {otherRegions.map(region => (
            <li key={region.legacyResourceId}>
              <S.RegionLink href={getRegionUrl(region)}>
                {region.name}
              </S.RegionLink>
            </li>
          ))}
        </S.OtherRegions>
      </S.Regions>

      <S.FooterTopContainer>
        <LogoLink />
        <S.FooterTop>
          <S.FooterPhones data-testid="footer-phones">
            <S.PhoneNumber
              $variant="4"
              as="a"
              href={makeHrefForPhone(CONTACT_US.PHONE)}
            >
              {CONTACT_US.PHONE}
            </S.PhoneNumber>
          </S.FooterPhones>
          <S.MailAddress
            $variant="5"
            as="a"
            href={`mailto:${CONTACT_US.EMAIL}`}
            target="_blank"
            data-testid="mail-footer-social"
          >
            {CONTACT_US.EMAIL}
          </S.MailAddress>

          <S.FooterSocials data-testid="footer-socials">
            <S.FooterSocial
              href={CONTACT_US.FACEBOOK}
              target="_blank"
              $iconName="facebook-footer.svg"
              data-testid="facebook-footer-social"
              aria-label="Facebook"
            />
            <S.FooterSocial
              href={CONTACT_US.INSTAGRAM}
              target="_blank"
              $iconName="instagram-footer.svg"
              data-testid="instagram-footer-social"
              aria-label="Instagram"
            />
          </S.FooterSocials>
        </S.FooterTop>
      </S.FooterTopContainer>

      <S.FooterDivider />

      <S.FooterLinks data-testid="footer-links">
        <S.FooterChangeLang>
          <ChangeLang
            typography={Typography.Caption}
            typographyProps={{ $variant: '2', className: 'footer__link--lang' }}
            hasIcon
          />
        </S.FooterChangeLang>

        <S.FooterInfo>
          <Typography.Caption
            $variant="2"
            as={CustomLink}
            href={ROUTES.help}
            target="_blank"
          >
            {t('common.help')}
          </Typography.Caption>
          <Typography.Caption
            $variant="2"
            as={CustomLink}
            href={ROUTES.about}
            target="_blank"
          >
            {t('common.about_project')}
          </Typography.Caption>
          <Typography.Caption
            $variant="2"
            as={CustomLink}
            href={ROUTES.rules}
            target="_blank"
          >
            {t('common.placement_rules')}
          </Typography.Caption>
          <Typography.Caption
            $variant="2"
            as={CustomLink}
            href={ROUTES.terms}
            target="_blank"
          >
            {t('common.terms_of_use')}
          </Typography.Caption>
          <Typography.Caption
            $variant="2"
            as={CustomLink}
            href={ROUTES.privacy}
            target="_blank"
          >
            {t('common.privacy_policy')}
          </Typography.Caption>
          <Typography.Caption
            $variant="2"
            as={CustomLink}
            href={ROUTES.proposal}
            target="_blank"
          >
            {t('common.proposal')}
          </Typography.Caption>
        </S.FooterInfo>
      </S.FooterLinks>

      <NoSsr>
        <Typography.Caption data-testid="footer-copyright">
          © 2008-{new Date().getFullYear()} Digital Classifieds MMC. VÖEN:
          1405631661
        </Typography.Caption>
      </NoSsr>

      <S.FooterResponsibility data-testid="footer-responsibility">
        {t('common.administration_is_not_responsible')}
      </S.FooterResponsibility>
    </S.FooterBox>
  );
};
